import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Typography, MenuItem, Select, FormControl, InputLabel,
  Paper, Button, Stack, Chip, Divider, Alert, CircularProgress
} from "@mui/material";
import axios from "axios";
import TableSnapshotAffissioni from "./TableSnapshotAffissioni";
import TableSnapshotDepositi from "./TableSnapshotDepositi"; // se non ti serve, rimuovi import + ramo depositi


const { REACT_APP_API_ENDPOINT = "" } = process.env;

export default function SnapshotDashboardManager() {
  const [tipo, setTipo] = useState("");                 // "affissioni" | "depositi"
  const [campagne, setCampagne] = useState([]);
  const [campagnaSelezionata, setCampagnaSelezionata] = useState("");
  const [snapshot, setSnapshot] = useState(null);

  const [loadingList, setLoadingList] = useState(false);
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [errorList, setErrorList] = useState(null);
  const [errorSnap, setErrorSnap] = useState(null);

  // 1) Carica lista campagne chiuse quando cambia il tipo
  useEffect(() => {
    setCampagne([]);
    setCampagnaSelezionata("");
    setSnapshot(null);
    setErrorList(null);
    if (!tipo) return;

    setLoadingList(true);
    axios
      .get(`${REACT_APP_API_ENDPOINT}/api/snapshot/campagne-chiuse?tipo=${encodeURIComponent(tipo)}`)
      .then(res => setCampagne(res.data || []))
      .catch(e => setErrorList(e?.response?.data?.message || e?.message || "Errore nel caricamento campagne"))
      .finally(() => setLoadingList(false));
  }, [tipo]);

  // 2) Carica lo snapshot (solo al click)
  const handleLoadSnapshot = () => {
    if (!campagnaSelezionata) return;
    setErrorSnap(null);
    setSnapshot(null);
    setLoadingSnap(true);
    axios
      .get(`${REACT_APP_API_ENDPOINT}/api/snapshot/${campagnaSelezionata}`)
      .then(res => setSnapshot(res.data))
      .catch(e => setErrorSnap(e?.response?.data?.message || e?.message || "Errore nel caricamento snapshot"))
      .finally(() => setLoadingSnap(false));
  };

  const isAffissioni = tipo === "affissioni";
  const isDepositi   = tipo === "depositi";

  // Manager: per affissioni prendiamo SEMPRE il ramo manager
  const branch = useMemo(() => {
    if (!snapshot) return null;
    return isAffissioni
      ? (snapshot?.snapshot?.manager || null)
      : (snapshot?.snapshot || null); // depositi è flat
  }, [snapshot, isAffissioni]);

  const campaignName = snapshot?.campaignName || snapshot?.denominazione || "Campagna";

  // Totali (se presenti nello snapshot manager/flat)
  const totals = useMemo(() => {
    if (!branch) return null;
    const totalOffices = typeof branch.totalOffices === "number" ? branch.totalOffices : null;
    const managed      = typeof branch.managed === "number" ? branch.managed :
                         typeof branch.totaleElementi === "number" ? branch.totaleElementi : null;
    const completed    = typeof branch.completed === "number" ? branch.completed :
                         typeof branch.completati === "number" ? branch.completati : null;
    const inProgress   = typeof branch.inProgress === "number" ? branch.inProgress : null;
    const notStarted   = typeof branch.notStarted === "number" ? branch.notStarted :
                         typeof branch.nonAvviati === "number" ? branch.nonAvviati : null;
    const totalAutoGestite = typeof branch.totalAutoGestite === "number" ? branch.totalAutoGestite : null;
    const notStartedCatalog = typeof branch.notStartedCatalog === "number" ? branch.notStartedCatalog : null;
    return { totalOffices, managed, completed, inProgress, notStarted, notStartedCatalog, totalAutoGestite };
  }, [branch]);

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>Snapshot Campagne — Manager</Typography>

      {/* FILTRI */}
      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr auto" }} gap={2}>
          <FormControl fullWidth>
            <InputLabel>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={tipo || ""}
              onChange={(e) => setTipo(e.target.value)}
            >
              <MenuItem value="affissioni">Affissioni</MenuItem>
              <MenuItem value="depositi">Depositi</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!campagne.length}>
            <InputLabel>Campagna</InputLabel>
            <Select
              label="Campagna"
              value={campagnaSelezionata || ""}
              onChange={(e) => setCampagnaSelezionata(e.target.value)}
            >
              {campagne.map(c => (
                <MenuItem key={c.campaignId} value={c.campaignId}>
                  {c.denominazione}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleLoadSnapshot}
            disabled={!campagnaSelezionata || loadingSnap}
          >
            {loadingSnap ? "Caricamento…" : "Carica snapshot"}
          </Button>
        </Box>

        {loadingList && (
          <Box mt={2} display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">Carico campagne…</Typography>
          </Box>
        )}

        {errorList && (
          <Alert severity="error" sx={{ mt: 2 }}>{String(errorList)}</Alert>
        )}
      </Paper>

      {/* CONTENUTO SNAPSHOT */}
      {snapshot && (
        <Paper sx={{ p: 2 }} variant="outlined">
          <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} justifyContent="space-between" spacing={2}>
            <Typography variant="h6">{campaignName}</Typography>
            {snapshot?.dataChiusura && (
              <Chip size="small" label={new Date(snapshot.dataChiusura).toLocaleString()} />
            )}
          </Stack>

          {branch ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {typeof totals?.totalOffices === "number" && <Chip label={`Uffici catalogo: ${totals.totalOffices}`} />}
                {typeof totals?.managed === "number" && <Chip color="info" label={`Gestiti: ${totals.managed}`} />}
                {typeof totals?.completed === "number" && <Chip color="success" label={`Completati: ${totals.completed}`} />}
                {typeof totals?.inProgress === "number" && <Chip color="warning" label={`In Progress: ${totals.inProgress}`} />}
                {typeof totals?.notStarted === "number" && <Chip color="error" label={`Non Avviati: ${totals.notStarted}`} />}
                {typeof totals?.notStartedCatalog === "number" && <Chip variant="outlined" label={`Catalogo non toccati: ${totals.notStartedCatalog}`} />}
                {typeof totals?.totalAutoGestite === "number" && <Chip color="success" label={`Auto gestite: ${totals.totalAutoGestite}`} />}
              </Stack>

              <Box mt={3}>
                {isAffissioni && (
                  <TableSnapshotAffissioni
                    branch={branch}
                    // fallback sicuro: la tabella NON andrà mai in .map su undefined
                    val={Array.isArray(branch?.dettaglioPerLuogo) ? branch.dettaglioPerLuogo : []}
                    campaignName={campaignName}
                    viewType="manager"
                  />
                )}

                {isDepositi && (
                  <TableSnapshotDepositi
                    val={snapshot?.snapshot?.dettaglioPerLuogo || []}
                    campaignName={campaignName}
                  />
                )}
              </Box>
            </>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>Nessun dato disponibile per questa campagna.</Alert>
          )}

          {errorSnap && <Alert severity="error" sx={{ mt: 2 }}>{String(errorSnap)}</Alert>}
        </Paper>
      )}
    </Box>
  );
}
